import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useStyles from "./styles";
import { PoiRun, usePoiRunDetail, usePoiRuns } from "../../api/poi";

const STATUS_COLOR: Record<string, "success" | "error" | "warning" | "info"> = {
  success: "success",
  failed: "error",
  aborted: "warning",
  running: "info",
};

/**
 * Human-readable explanations for the rejection codes the pipeline emits.
 * The raw code is still shown alongside — an operator escalating to a
 * developer needs the exact string, not a paraphrase.
 */
const REJECT_HELP: Record<string, string> = {
  outside_territory: "Coordonnées hors du pays sélectionné",
  lat_lng_swapped: "Latitude et longitude inversées dans le flux source",
  null_island: "Coordonnées vides (0,0)",
  low_precision: "Coordonnées arrondies au degré entier (~111 km d'erreur)",
  coords_out_of_range: "Coordonnées hors bornes valides",
  missing_geometry: "Aucune coordonnée fournie",
  no_identity: "Ni nom ni téléphone — fiche inaffichable",
  unmappable: "Structure inattendue : le connecteur n'a pas su lire la ligne",
  manual_discard: "Écarté manuellement depuis la file de vérification",
  projection_failed: "Échec de l'écriture vers la table sign",
};

const RunRow: React.FC<{ run: PoiRun }> = ({ run }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: detail, isLoading } = usePoiRunDetail(open ? run.id : null);

  const stats = run.stats ?? {};

  return (
    <>
      <TableRow hover>
        <TableCell width={48}>
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {run.source_key}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {(run.options?.countries ?? []).join(", ")}
          </Typography>
        </TableCell>

        <TableCell>
          <Chip
            size="small"
            label={run.status}
            color={STATUS_COLOR[run.status] ?? "default"}
            variant={run.status === "running" ? "filled" : "outlined"}
          />
        </TableCell>

        <TableCell>
          <Typography variant="caption">
            {run.started_at ? new Date(run.started_at).toLocaleString("fr-FR") : "—"}
          </Typography>
        </TableCell>

        <TableCell align="right">
          <Typography variant="caption">
            {run.duration_seconds !== null ? `${run.duration_seconds}s` : "—"}
          </Typography>
        </TableCell>

        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {stats.fetched ?? "—"}
        </TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {/* Unchanged rows are the checksum gate doing its job: on a stable
              feed this should be the large majority after the first run. */}
          <Tooltip title={t("poi.unchangedTip", "Ignorés car identiques au dernier import")}>
            <span>{stats.unchanged ?? "—"}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {stats.mapped ?? "—"}
        </TableCell>
        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
          <Typography
            variant="body2"
            color={(stats.rejected ?? 0) > 0 ? "error.main" : "text.primary"}
          >
            {stats.rejected ?? "—"}
          </Typography>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? undefined : "none" }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2 }}>
              {run.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" className={classes.monoSmall}>
                    {run.error}
                  </Typography>
                </Alert>
              )}

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {[
                  ["entities_new", t("poi.entitiesNew", "Nouvelles fiches")],
                  ["entities_matched", t("poi.entitiesMatched", "Rapprochées")],
                  ["review_queue", t("poi.toReview", "À vérifier")],
                  ["signs_created", t("poi.signsCreated", "Signs créés")],
                  ["signs_updated", t("poi.signsUpdated", "Signs mis à jour")],
                ].map(([key, label]) => (
                  <Chip
                    key={key}
                    size="small"
                    variant="outlined"
                    label={`${label}: ${stats[key as string] ?? 0}`}
                  />
                ))}
              </Box>

              {isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <>
                  {(detail?.rejections ?? []).length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t("poi.rejectionBreakdown", "Détail des rejets")}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 2 }}>
                        {detail.rejections.map((r: any) => (
                          <Box
                            key={r.reject_reason}
                            sx={{ display: "flex", gap: 1, alignItems: "baseline" }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, minWidth: 200 }}
                              className={classes.monoSmall}
                            >
                              {r.reject_reason}
                            </Typography>
                            <Typography variant="body2" color="error.main">
                              {r.total}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {REJECT_HELP[r.reject_reason] ?? ""}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  {(detail?.rejection_samples ?? []).length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t("poi.rejectionSamples", "Exemples de lignes rejetées")}
                      </Typography>
                      <TableContainer component={Paper} className={classes.tableWrap}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ref</TableCell>
                              <TableCell>{t("poi.reason", "Motif")}</TableCell>
                              <TableCell>lat</TableCell>
                              <TableCell>lng</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.rejection_samples.slice(0, 15).map((s: any, i: number) => (
                              <TableRow key={`${s.source_ref}-${i}`}>
                                <TableCell className={classes.monoSmall}>
                                  {s.source_ref}
                                </TableCell>
                                <TableCell className={classes.monoSmall}>
                                  {s.reject_reason}
                                </TableCell>
                                <TableCell className={classes.monoSmall}>
                                  {s.latitude ?? "—"}
                                </TableCell>
                                <TableCell className={classes.monoSmall}>
                                  {s.longitude ?? "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const RunsTab: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);

  const { runs, pagination, isLoading } = usePoiRuns({
    page: page + 1,
    per_page: perPage,
  });

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t(
          "poi.runsIntro",
          "Chaque exécution est tracée. Un import qui échoue ne projette rien : les données existantes restent intactes."
        )}
      </Typography>

      <TableContainer component={Paper} className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t("poi.source", "Source")}</TableCell>
              <TableCell>{t("poi.status", "Statut")}</TableCell>
              <TableCell>{t("poi.startedAt", "Démarré")}</TableCell>
              <TableCell align="right">{t("poi.duration", "Durée")}</TableCell>
              <TableCell align="right">{t("poi.fetched", "Lus")}</TableCell>
              <TableCell align="right">{t("poi.unchanged", "Inchangés")}</TableCell>
              <TableCell align="right">{t("poi.mapped", "Traités")}</TableCell>
              <TableCell align="right">{t("poi.rejected", "Rejetés")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : runs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className={classes.emptyState}>
                  {t("poi.noRuns", "Aucun import lancé pour l'instant.")}
                </TableCell>
              </TableRow>
            ) : (
              runs.map((run) => <RunRow key={run.id} run={run} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => {
            setPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage={t("poi.rowsPerPage", "Lignes par page :")}
          sx={{
            minWidth: 0,
            overflow: "hidden",
            "& .MuiTablePagination-toolbar": {
              flexWrap: "wrap",
              justifyContent: "flex-end",
              rowGap: "4px",
              minHeight: "auto",
              paddingLeft: 0,
            },
            "& .MuiTablePagination-displayedRows": { margin: 0 },
            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              display: { xs: "none", sm: "block" },
            },
          }}
        />
      )}
    </Box>
  );
};
